import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom'
import AuthFlow from './components/Auth/AuthFlow'
import OAuthCallback from './components/Auth/OAuthCallback'
import PrivateRoute from './components/PrivateRoute'
import MainLayout from './components/Layout/MainLayout'
import Dashboard from './components/Dashboard/Dashboard'
import TestSeriesView from './features/test-series/pages/TestSeriesView'
import TestSeriesListPage from './features/test-series/pages/TestSeriesListPage'
import SeriesAccessGuard from './features/test-series/components/SeriesAccessGuard'
import MyPerformance from './features/analytics/components/MyPerformance'
import { PastExamsDashboard } from './features/analytics/components/PastExamsDashboard'
import { UnitDetailPage, TopicDetailPage, TopicComparisonPage, TrendingTopicsPage } from './features/analytics/pages'
import {
  PYQFormPage,
  PYQBrowserPage,
  PYQByExamPage,
  PYQBySyllabusPage,
  PYQExamBrowserPage,
  PYQSyllabusBrowserPage,
} from './features/pyq'
import { TestInstructionsPage, MockTestPage, TestResultsPage, AttemptReviewPage } from './features/mock-test'
import { ProfileCompletionPage } from './features/onboarding'
import { ProfilePage } from './features/profile'
import { AccountPage } from './features/account'
import { ContactUsPage } from './features/support'
import { SolutionBrowserPage } from './features/solution-browser'
import { PageTracker } from './shared/hooks/usePageTracking'

// Layout wrapper that includes page tracking
function RootLayout() {
  return (
    <>
      <PageTracker />
      <Outlet />
    </>
  )
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Public Routes
      { path: '/login', element: <AuthFlow /> },
      { path: '/signup', element: <Navigate to="/login" replace /> },
      { path: '/callback', element: <OAuthCallback /> },

      // Solution Browser - Full Screen (outside MainLayout, accessed via QR code)
      {
        path: '/view/:testCode/solutions',
        element: (
          <PrivateRoute>
            <SolutionBrowserPage />
          </PrivateRoute>
        ),
      },

      // Onboarding Route - Protected but outside MainLayout
      {
        path: '/onboarding',
        element: (
          <PrivateRoute requireCompleteProfile={false}>
            <ProfileCompletionPage />
          </PrivateRoute>
        ),
      },

      // Mock Test Routes - Full Screen (outside MainLayout)
      {
        path: '/mock-test/:testId/instructions',
        element: (
          <PrivateRoute>
            <TestInstructionsPage />
          </PrivateRoute>
        ),
      },
      {
        path: '/mock-test/:testId/attempt/:attemptId',
        element: (
          <PrivateRoute>
            <MockTestPage />
          </PrivateRoute>
        ),
      },
      {
        path: '/mock-test/:testId/results/:attemptId',
        element: (
          <PrivateRoute>
            <TestResultsPage />
          </PrivateRoute>
        ),
      },
      {
        path: '/mock-test/:testId/review/:attemptId',
        element: (
          <PrivateRoute>
            <AttemptReviewPage />
          </PrivateRoute>
        ),
      },

      // Question Bank Browse - Full Screen (outside MainLayout)
      {
        path: '/question-bank/browse',
        element: (
          <PrivateRoute>
            <PYQBrowserPage />
          </PrivateRoute>
        ),
      },
      {
        path: '/question-bank/exam-browser',
        element: (
          <PrivateRoute>
            <PYQExamBrowserPage />
          </PrivateRoute>
        ),
      },
      {
        path: '/question-bank/syllabus-browser',
        element: (
          <PrivateRoute>
            <PYQSyllabusBrowserPage />
          </PrivateRoute>
        ),
      },

      // Analytics Detail Pages - Full Screen (outside MainLayout)
      {
        path: '/analytics/past-exams/unit/:unitCode',
        element: (
          <PrivateRoute>
            <UnitDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: '/analytics/past-exams/topic/:topicCode',
        element: (
          <PrivateRoute>
            <TopicDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: '/analytics/past-exams/compare',
        element: (
          <PrivateRoute>
            <TopicComparisonPage />
          </PrivateRoute>
        ),
      },
      {
        path: '/analytics/past-exams/trending',
        element: (
          <PrivateRoute>
            <TrendingTopicsPage />
          </PrivateRoute>
        ),
      },

      // Protected Routes with MainLayout
      {
        path: '/',
        element: (
          <PrivateRoute>
            <MainLayout />
          </PrivateRoute>
        ),
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: 'dashboard', element: <Dashboard /> },
          { path: 'test-series', element: <TestSeriesListPage /> },
          {
            path: 'test-series/:seriesCode',
            element: (
              <SeriesAccessGuard>
                <TestSeriesView />
              </SeriesAccessGuard>
            ),
          },
          { path: 'question-bank', element: <PYQFormPage /> },
          { path: 'question-bank/by-exam', element: <PYQByExamPage /> },
          { path: 'question-bank/by-syllabus', element: <PYQBySyllabusPage /> },
          // Old syllabus and by-topic pages are replaced by Browse PYQ by Syllabus
          { path: 'syllabus', element: <Navigate to="/question-bank/by-syllabus" replace /> },
          { path: 'question-bank/by-topic', element: <Navigate to="/question-bank/by-syllabus" replace /> },
          { path: 'analytics/my-performance', element: <MyPerformance /> },
          { path: 'analytics/past-exams', element: <PastExamsDashboard /> },
          { path: 'study-materials', element: <div className="p-4">Study Materials - Coming Soon</div> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'account', element: <AccountPage /> },
          { path: 'contact', element: <ContactUsPage /> },
        ],
      },

      // Catch all
      { path: '*', element: <Navigate to="/dashboard" replace /> },
    ],
  },
])

function App() {
  return <RouterProvider router={router} />
}

export default App
